import { useMemo } from "react";
import type { HistoryPoint } from "../types";
import { extractSignalEvents } from "../lib/signals";
import { formatAgo, formatPrice } from "../lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { cn } from "@/lib/utils";

interface Props {
  history: HistoryPoint[] | null;
  now: number;
  limit?: number;
}

export function RecentSignals({ history, now, limit = 12 }: Props) {
  const events = useMemo(() => {
    if (!history) return [];
    return extractSignalEvents(history).reverse().slice(0, limit);
  }, [history, limit]);

  return (
    <Card className="flex min-h-0 flex-1 flex-col">
      <CardHeader className="flex flex-row items-baseline justify-between space-y-0 border-b p-3">
        <CardTitle>Recent Signals</CardTitle>
        <span className="text-[11px] text-muted-foreground">
          last {limit}
        </span>
      </CardHeader>
      <CardContent className="min-h-0 flex-1 overflow-auto p-1">
        {!history ? (
          <div className="p-2 italic text-muted-foreground">Loading…</div>
        ) : events.length === 0 ? (
          <div className="p-2 italic text-muted-foreground">
            No signals in lookback window.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Side</TableHead>
                <TableHead className="text-right">Entry</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((e) => (
                <TableRow key={`${e.time}-${e.kind}`}>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatAgo(e.time, now)}
                  </TableCell>
                  <TableCell className="font-mono-tabular text-[10px] uppercase tracking-wider">
                    <span
                      className={cn(
                        "rounded px-1.5 py-0.5",
                        e.kind === "recovery"
                          ? "bg-accent text-accent-foreground"
                          : "bg-muted text-muted-foreground"
                      )}
                    >
                      {e.kind === "recovery" ? "REC" : "INIT"}
                    </span>
                  </TableCell>
                  <TableCell
                    className={cn(
                      "font-mono-tabular text-xs font-semibold",
                      e.side === "BUY" ? "text-buy" : "text-sell"
                    )}
                  >
                    {e.side}
                  </TableCell>
                  <TableCell className="text-right font-mono-tabular text-xs">
                    ${formatPrice(e.price)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
